<template>
  <section class="section-container" v-if="!is_supplier">
    <div class="content-wrapper">
      <div class="content-heading" v-if="tender.id">
        {{ $t('tender.edit') }}
      </div>
      <div class="content-heading" v-else>
        {{ $t('tender.add') }}
      </div>
      <div class="row">
        <div class="col-xl-12">
          <div class="card card-default">
            <div class="card-body">

              <div class="row">
                <div class="col-xl-4">

                  <div class="form-group">
                    <label class="col-form-label" for="start">{{ $t('tender.start') }} *</label>
                    <datetime
                      :class="{'parsley-error': errors.start}"
                      :hour-step="1"
                      :minute-step="15"
                      :phrases="{ok: 'Continue', cancel: 'Exit'}"
                      :week-start="1"
                      auto
                      format="dd.MM.yyyy HH:mm"
                      id="start"
                      input-class="form-control text"
                      :placeholder="$t('tender.date-time')"
                      type="datetime"
                      value-zone="local"
                      use24-hour
                      v-model="tender.start">
                    </datetime>
                    <ul class="parsley-errors-list filled" v-if="errors.start">
                      <li class="parsley-required">{{ errors.start[0] }}</li>
                    </ul>
                  </div>

                  <div class="form-group">
                    <label class="col-form-label" for="finish">{{ $t('tender.end') }} *</label>
                    <datetime
                      :class="{'parsley-error': errors.finish}"
                      :hour-step="1"
                      :minute-step="15"
                      :phrases="{ok: 'Continue', cancel: 'Exit'}"
                      :week-start="1"
                      auto
                      format="dd.MM.yyyy HH:mm"
                      id="finish"
                      input-class="form-control text"
                      :placeholder="$t('tender.date-time')"
                      type="datetime"
                      value-zone="local"
                      use24-hour
                      v-model="tender.finish">
                    </datetime>
                    <ul class="parsley-errors-list filled" v-if="errors.finish">
                      <li class="parsley-required">{{ errors.finish[0] }}</li>
                    </ul>
                  </div>

                  <div class="form-group" v-if="groups.length > 1">
                    <label>{{ $t('warehouse.group') }} *</label>
                    <multiselect
                      :class="{'parsley-error': errors.group}"
                      :options="groups"
                      id="group"
                      label="name"
                      :placeholder="$t('tender.select-group')"
                      track-by="id"
                      @select="select_group"
                      v-model="tender.group">
                      <template slot="noOptions">{{ $t('global.no-options') }}</template>
                    </multiselect>
                    <ul class="parsley-errors-list filled" v-if="errors.group">
                      <li class="parsley-required">{{ errors.group[0] }}</li>
                    </ul>
                  </div>

                  <div class="form-group" v-if="warehouses.length > 0">
                    <label for="warehouse">{{ $t('tender.warehouse') }} *</label>
                    <multiselect
                      :class="{'parsley-error': errors.warehouse}"
                      :options="warehouses"
                      id="warehouse"
                      label="name"
                      :placeholder="$t('tender.select')"
                      :close-on-select="true"
                      track-by="id"
                      @input="warehouse_change()"
                      v-model="user_warehouse">
                      <template slot="noOptions">{{ $t('global.no-options') }}</template>
                    </multiselect>
                    <ul class="parsley-errors-list filled" v-if="errors.warehouse">
                      <li class="parsley-required">{{ errors.warehouse[0] }}</li>
                    </ul>
                    <span v-if="warehouse_changed">
                      <button
                        class="btn btn-outline-warning mt-2 btn-sm"
                        @click="confirm_warehouse"
                      >{{ $t('tender.approve') }}
                      </button>
                    </span>
                  </div>

                  <div class="form-group">
                    <label>{{ $t('tender.additional-info') }}</label>
                    <textarea
                      :class="{'parsley-error': errors.additional_info}"
                      class="form-control"
                      :placeholder="$t('tender.tender-additional-info')"
                      rows="4"
                      v-model="tender.additional_info">
                    </textarea>
                    <ul class="parsley-errors-list filled" v-if="errors.additional_info">
                      <li class="parsley-required">{{ errors.additional_info[0] }}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-xl-12">
                  <div class="dataTables_wrapper table-responsive container-fluid dt-bootstrap4 no-footer">
                    <h4>{{ $t('tender.pre-order') }}</h4>
                    <search @make_search="make_search"/>

                    <table class="table table-striped dataTable my-4 w-100" id="tenderChosen">
                      <thead>
                      <tr>
                        <th></th>
                        <th
                          :class="{
                            'sorting_asc': ordering === 'part__code',
                            'sorting_desc': ordering === '-part__code'}"
                          @click="order('part__code')">{{ $t('tender.part-code') }}
                        </th>
                        <th
                          :class="{
                            'sorting_asc': ordering === 'part__name',
                            'sorting_desc': ordering === '-part__name'}"
                          @click="order('part__name')">{{ $t('tender.part-name') }}
                        </th>
                        <th
                          :class="{
                            'sorting_asc': ordering === 'warehouse__name',
                            'sorting_desc': ordering === '-warehouse__name'}"
                          @click="order('warehouse__name')">{{ $t('tender.warehouse') }}
                        </th>
                        <th
                          :class="{
                              'sorting_asc': ordering === 'customer_number',
                              'sorting_desc': ordering === '-customer_number'
                            }"
                            @click="order('customer_number')"
                        >{{ $t('tender.transport') }}</th>
                        <th
                          :class="{
                            'sorting_asc': ordering === 'customer_name',
                            'sorting_desc': ordering === '-customer_name'}"
                          @click="order('customer_name')">{{ $t('details.customer_name') }}
                        </th>
                        <th
                          :class="{
                            'sorting_asc': ordering === 'creator__name',
                            'sorting_desc': ordering === '-creator__name'
                          }"
                          @click="order('creator__name')">{{ $t('details.creator') }}
                        </th>
                        <th
                          :class="{
                            'sorting_asc': ordering === 'order_number',
                            'sorting_desc': ordering === '-order_number'
                          }"
                          @click="order('order_number')">{{ $t('details.order_number') }}
                        </th>
                        <th
                            :class="{
                            'sorting_asc': ordering === 'part__unit__code',
                            'sorting_desc': ordering === '-part__unit__code'}"
                            @click="order('part__unit__code')">{{ $t('tender.unit') }}
                        </th>
                        <th
                          :class="{
                            'sorting_asc': ordering === 'replenishment_qty',
                            'sorting_desc': ordering === '-replenishment_qty'
                          }"
                          @click="order('replenishment_qty')">{{ $t('details.replenishment-qty') }}
                        </th>
                        <th>{{ $t('tender.order-qty') }}</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr :key="c.id" class="gradeX" v-for="(c, index) in chosen">
                        <td>
                          <button
                            class="icon-close text-danger link-unstyled btn btn-link"
                            :title="$t('tender.delete')"
                            @click="delete_chosen(c.id, index)">
                          </button>
                        </td>
                        <td>
                            <span>{{ c.part_code }}</span>
                        </td>
                        <td>{{ c.part_name }}
                          <button
                            class="btn btn-link pl-1 pt-1"
                            @click="open_modal(c.part_name, c.part_codes_tags)"
                            v-if="c.part_codes_tags">
                            <i class="fa icon-info ml-1"></i>
                          </button>
                        </td>
                        <td>{{ c.warehouse_name_code }}</td>
                        <td>{{ c.customer_number }}</td>
                        <td>{{ c.customer_name }}</td>
                        <td>{{ c.creator_name }}</td>
                        <td>{{ c.order_number }}</td>
                        <td>{{ c.part_unit_code }}</td>
                        <td>{{ c.replenishment_qty > 0 ? c.replenishment_qty : 0 }}</td>
                        <td>
                          <div class="inline">
                            <div class="form-group mb-0">
                              <input
                                class="form-control wd-xs"
                                @change="save_chosen(c)"
                                :placeholder="$t('tender.qty')"
                                type="number"
                                v-model="c.qty">
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td colspan="9"></td>
                        <td class="text-bold">{{ $t('tender.total') }}</td>
                        <td class="text-bold">{{ totalReplenishmentQtyChosen }}</td>
                        <td class="text-bold">{{ totalChosen }}</td>
                      </tr>
                      <tr :key="d.id" class="gradeX" v-for="(d, index) in details">
                        <td>
                          <button
                            class="icon-plus text-success link-unstyled btn btn-link"
                            :title="$t('tender.add')"
                            @click="choose_detail(d, index)"
                            v-if="d.qty > 0"
                          >
                          </button>
                        </td>
                        <td>
                            <span>{{ d.part_code }}</span>
                        </td>
                        <td>{{ d.part_name }}
                          <button
                            class="btn btn-link pl-1 pt-1"
                            @click="open_modal(d.part_name, d.part_codes_tags)"
                            v-if="d.part_codes_tags">
                            <i class="fa icon-info ml-1"></i>
                          </button>
                        </td>
                        <td>{{ d.warehouse_name_code }}</td>
                        <td>{{ d.customer_number }}</td>
                        <td>{{ d.customer_name }}</td>
                        <td>{{ d.creator_name }}</td>
                        <td>{{ d.order_number }}</td>
                        <td>{{ d.part_unit_code }}</td>
                        <td>{{ d.replenishment_qty > 0 ? d.replenishment_qty : 0 }}</td>
                        <td>
                          <div class="inline">
                            <div class="form-group mb-0">
                              <input
                                class="form-control wd-xs"
                                disabled
                                :placeholder="$t('tender.qty')"
                                type="number"
                                v-model="d.qty">
                            </div>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                    <pagination
                      :page="page"
                      :pages="pages"
                      :count="count"
                      :from="(page - 1) * 50 + 1"
                      :to="(page - 1) * 50 + details.length"
                      @paginate="paginate"
                    />
                    <hr>
                    <div class="text-right">
                      <router-link :to="{name: 'tenders'}" class="btn btn-sm btn-secondary wd-xs" type="cancel">
                        <span>{{ $t('tender.cancel') }}</span>
                      </router-link>
                      <button
                        @click="save_draft"
                        class="btn btn-sm btn-outline-success wd-xs"
                        type="submit">
                        {{ $t('tender.save') }}
                      </button>
                      <button
                        @click="save_tender"
                        class="btn btn-sm btn-success"
                        type="submit">
                        {{ $t('tender.start-tender') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <modal v-if="showModal">
      <h4 class="modal-title" slot="header-text">{{ $t('modal.header.text') }}</h4>
      <div class="modal-body" slot="modal-body">
        <p class="text-bold">{{ $t('modal.section1.header') }}</p>
        <p>{{ modal_data_1 }}</p>
        <p class="text-bold">{{ $t('modal.section2.header') }}</p>
        <p>{{ modal_data_2}}</p>
      </div>
      <div slot="modal_buttons">
        <button class="btn btn-secondary" type="button" @click="close_modal()">{{ $t('modal.close') }}</button>
      </div>
    </modal>

  </section>
</template>

<script>
import api from '@/api'
import message from '@/message'
import { Datetime } from 'vue-datetime'
import Pagination from '@/components/Pagination.vue'
import Search from '@/components/Search.vue'
import {parseApiError} from '@/utils'
import Modal from '@/components/Modal.vue'
import Multiselect from 'vue-multiselect'

const getData = (id, route, next) => {
  const search = route.query.search
  const ordering = route.query.ordering || 'pk'
  const page = parseInt(route.query.page) || 1
  const start = route.query.start
  const finish = route.query.finish
  const warehouse = route.query.warehouse || null
  const additional = route.query.additional || ''
  const tenderId = route.query.tender_id || null
  const group = route.query.group || null

  if (!id && tenderId) {
    id = tenderId
  }

  if (id) {
    Promise.all([
      api.get(`/tenders/${id}/`),
      api.get(`/details/`, {
        params: {
          page_size: 500,
          tender: id,
          search: search,
          ordering: ordering,
          page: 1
        }
      }),
      api.get('/warehouses/?is_active=true&page_size=1000'),
      api.get(`/profile/`)
    ])
      .then(([tender, chosen, warehouses, profile]) => {
        let rows = {}
        api.get(`/details/`, {
          params: {
            status: 'PREPARED',
            // warehouse: tender.data.warehouse,
            page_size: 50,
            search: search,
            ordering: ordering,
            page: page
          }
        })
          .then(resp => {
            rows = resp.data
            rows.results.forEach(r => {
              if (r.qty === null) {
                r.qty = r.replenishment_qty + r.replacement_qty > 0 ? r.replenishment_qty + r.replacement_qty : 0
              }
            })

            let rowsChosen = chosen.data.results
            rowsChosen.forEach(r => {
              if (r.qty === null) {
                r.qty = r.replenishment_qty + r.replacement_qty > 0 ? r.replenishment_qty + r.replacement_qty : 0
              }
            })

            let userWarehouses = []
            warehouses.data.results.forEach(w => {
              if (w.group === tender.data.group) {
                userWarehouses.push(w)
              }
            })

            tender.data.warehouse = {
              'id': tender.data.warehouse,
              'name': tender.data.warehouse_name
            }

            tender.data.group = {
              'id': tender.data.group,
              'name': tender.data.group_name
            }

            next({
              tender: tender.data,
              user_warehouse: tender.data.warehouse,
              details: rows.results,
              search: search,
              ordering: ordering,
              chosen: rowsChosen,
              page: page,
              pages: rows ? rows.pages : 1,
              warehouses: userWarehouses,
              groups: profile.data.results ? profile.data.results[0].warehouse_groups_options : []
            })
          })
      })
  } else {
    Promise.all([
      api.get('/details/', {
        params: {
          status: 'PREPARED',
          page_size: 50,
          search: search,
          ordering: ordering,
          page: page
        }
      }),
      api.get('/warehouses/?is_active=true&page_size=1000'),
      api.get(`/profile/`)
    ])
      .then(([details, warehouses, profile]) => {
        let rows = details.data.results
        rows.forEach(r => {
          r.qty = r.replenishment_qty + r.replacement_qty > 0 ? r.replenishment_qty + r.replacement_qty : 0
        })

        let groups = profile.data.results ? profile.data.results[0].warehouse_groups_options : []

        next({
          tender: {
            start: start,
            finish: finish,
            additional_info: additional,
            warehouse: warehouse,
            group: groups && groups.length === 1 ? { id: groups[0].id, name: groups[0].name } : group
          },
          details: rows,
          search: search,
          ordering: ordering,
          page: page,
          pages: details.data.pages,
          count: details.data.count,
          warehouses: groups && groups.length === 1 ? warehouses.data.results : [],
          groups: groups
        })
      })
  }
}

export default {
  components: {
    datetime: Datetime,
    pagination: Pagination,
    search: Search,
    modal: Modal,
    Multiselect
  },
  data () {
    return {
      tender: {
        id: null,
        start: null,
        finish: null,
        additional_info: '',
        warehouse: null
      },
      details: [],
      warehouses: [],
      groups: [],
      chosen: [],
      search: null,
      user_warehouse: null,
      ordering: null,
      count: null,
      errors: {},
      page: null,
      pages: null,
      showModal: false,
      modal_data_1: null,
      modal_data_2: null,
      warehouse_changed: false
    }
  },
  mounted () {
    this.$parent.menu = 'tenders'
    this.$parent.submenu = ''
    this.$i18n.locale = this.$store.getters.language
  },
  computed: {
    is_supplier () {
      return this.$store.getters.user_group === 'Supplier'
    },
    totalReplacementQtyChosen () {
      let total = 0
      for (let i = 0; i < this.chosen.length; i++) {
        total += this.chosen[i].replacement_qty > 0 ? this.chosen[i].replacement_qty : 0
      }
      return total
    },
    totalReplenishmentQtyChosen () {
      let total = 0
      for (let i = 0; i < this.chosen.length; i++) {
        total += this.chosen[i].replenishment_qty > 0 ? this.chosen[i].replenishment_qty : 0
      }
      return total
    },
    totalQtyChosen () {
      let total = 0
      for (let i = 0; i < this.chosen.length; i++) {
        total += this.chosen[i].replacement_qty + this.chosen[i].replenishment_qty > 0
          ? this.chosen[i].replacement_qty + this.chosen[i].replenishment_qty : 0
      }
      return total
    },
    totalChosen () {
      let total = 0
      this.chosen.forEach(c => {
        if (c.qty && c.qty > 0) {
          total += Number(c.qty)
        }
      })
      return total
    }
  },
  methods: {
    warehouse_change () {
      if (!this.tender.warehouse) {
        this.refresh_details()
      } else {
        this.warehouse_changed = true
      }
    },
    refresh_details () {
      this.tender.warehouse = this.user_warehouse
      if (this.tender.id && Number(this.tender.id) > 0) {
        api.get(`/details/`, {
          params: {
            page_size: 500,
            tender: this.tender.id,
            page: 1
          }
        })
          .then(rows => {
            rows.data.results.forEach(r => {
              api.patch(`/details/${r.id}/`, {
                ...r,
                status: 'PREPARED',
                tender: null
              })
            })
          })
      }

      let params = {
        status: 'PREPARED',
        // warehouse: this.user_warehouse ? this.user_warehouse.id : null,
        page_size: 50,
        search: this.search,
        ordering: this.ordering,
        page: this.page
      }

      // if (this.user_warehouse) {
      //   params.warehouse = this.user_warehouse.id
      // }

      api.get(`/details/`, {params: params})
        .then(rows => {
          let rowsData = rows.data.results
          rowsData.forEach(r => {
            r.qty = r.replenishment_qty + r.replacement_qty > 0 ? r.replenishment_qty + r.replacement_qty : 0
          })
          this.details = rowsData
          this.chosen = []
          this.page = 1
          this.pages = rows.data.pages
        })
    },
    confirm_warehouse () {
      this.$swal({
        type: 'warning',
        title: this.$i18n.t('tender.are-you-sure'),
        text: this.$i18n.t('tender.are-you-sure-text-cancel'),
        showCancelButton: true
      })
        .then((choice) => {
          if (choice.value) {
            this.errors = {}
            this.refresh_details()
          } else {
            this.user_warehouse = this.tender.warehouse
          }
        })
      this.warehouse_changed = false
    },
    select_group (g) {
      this.errors = {}
      this.tender.warehouse = null
      api.get('/warehouses/', {
        params: {
          is_active: true,
          page_size: 1000,
          // group: g.id
        }
      })
        .then(r => {
          this.warehouses = r.data.results
        })
    },
    close_modal () {
      this.showModal = false
    },
    open_modal (data1, data2) {
      this.showModal = true
      this.modal_data_1 = data1
      this.modal_data_2 = data2
    },
    save_chosen (chosen) {
      chosen['replenishment_qty'] = Number(chosen.qty)
      api.patch(`/details/${chosen.id}/`, {
        ...chosen
      })
    },
    choose_detail (d, index) {
      this.errors = {}
      if (this.tender.id) {
        api.patch(`/tenders/${this.tender.id}/`, {
          start: this.tender.start,
          finish: this.tender.finish,
          status: 'DRAFT',
          warehouse: this.tender.warehouse ? this.tender.warehouse.id : null,
          group: this.tender.group ? this.tender.group.id : null,
          additional_info: this.tender.additional_info
        })
          .then(() => {
            d.status = 'WAITING'
            d.tender = this.tender.id
            api.patch(`/details/${d.id}/`, d)
              .then(() => {
                this.chosen.push(d)
                this.$delete(this.details, index)
              })
          })
          .catch(error => {
            if (error.response && error.response.status === 400) {
              this.errors = parseApiError(error, this.$i18n)
              window.scrollTo(0, 0)
            }
          })
      } else {
        api.post(`/tenders/`, {
          start: this.tender.start,
          finish: this.tender.finish,
          warehouse: this.tender.warehouse ? this.tender.warehouse.id : null,
          group: this.tender.group ? this.tender.group.id : null,
          status: 'DRAFT',
          additional_info: this.tender.additional_info
        })
          .then(resp => {
            this.tender.id = resp.data.id
            d.status = 'WAITING'
            d.tender = resp.data.id
            api.patch(`/details/${d.id}/`, d)
              .then(() => {
                this.chosen.push(d)
                this.$delete(this.details, index)
              })
          })
          .catch(error => {
            if (error.response && error.response.status === 400) {
              this.errors = parseApiError(error, this.$i18n)
              window.scrollTo(0, 0)
            }
          })
      }
    },
    paginate (p) {
      api.get(`/details/`, {
        params: {
          status: 'PREPARED',
          page_size: 50,
          search: this.search,
          ordering: this.ordering,
          page: p
        }
      })
        .then(resp => {
          let rows = resp.data.results
          rows.forEach(r => {
            r.qty = r.replenishment_qty + r.replacement_qty > 0 ? r.replenishment_qty + r.replacement_qty : 0
          })
          this.details = rows
          this.pages = resp.data.pages
          this.count = resp.data.count
          this.page = p
        })
    },
    delete_chosen (id, index) {
      api.patch(`/details/${id}/`, {
        ...this.chosen[index],
        status: 'PREPARED',
        tender: null
      })
      this.details.push(this.chosen[index])
      this.chosen.splice(index, 1)
    },
    order (o) {
      if (o === this.ordering) {
        this.$router.push({
          query: {
            search: this.search,
            ordering: '-' + o,
            page: 1,
            start: this.tender.start,
            finish: this.tender.finish,
            warehouse: this.tender.warehouse,
            group: this.tender.group,
            additional: this.tender.additional_info,
            tender_id: this.tender.id
          }
        })
      } else {
        this.$router.push({
          query: {
            search: this.search,
            ordering: o,
            page: 1,
            start: this.tender.start,
            finish: this.tender.finish,
            warehouse: this.tender.warehouse,
            group: this.tender.group,
            additional: this.tender.additional_info,
            tender_id: this.tender.id
          }
        })
      }
    },
    make_search () {
      this.$router.push({
        query: {
          search: this.search,
          ordering: this.ordering,
          page: 1,
          start: this.tender.start,
          finish: this.tender.finish,
          warehouse: this.tender.warehouse,
          group: this.tender.group,
          additional: this.tender.additional_info,
          tender_id: this.tender.id
        }
      })
    },
    save_draft () {
      if (this.tender.id) {
        this.errors = {}
        let hasError = false
        let chosenQty = 0

        for (let i = 0; i < this.chosen.length; i++) {
          api.patch(`/details/${this.chosen[i].id}/`, {
            ...this.chosen[i]
          })
          chosenQty += parseInt(this.chosen[i].qty)
        }

        if (this.chosen.length === 0 || chosenQty === 0) {
          hasError = true
          message.error(this.$i18n.t('tender.positive-qty'))
        }

        if (this.tender.start) {
          let current = new Date()
          let check = new Date(this.tender.start)

          if (check < current) {
            hasError = true
            this.errors['start'] = [this.$i18n.t('tender.start-time-should-be-at-future')]
          }
        }

        if (hasError === false) {
          api.patch(`/tenders/${this.tender.id}/`, {
            start: this.tender.start,
            finish: this.tender.finish,
            warehouse: this.tender.warehouse ? this.tender.warehouse.id : null,
            group: this.tender.group ? this.tender.group.id : null,
            status: 'DRAFT',
            additional_info: this.tender.additional_info
          })
            .then(() => {
              setTimeout(() => {
                message.success(this.$i18n.t('tender.draft-updated'))
                this.$router.push({name: 'tenders'})
              }, 1000)
            })
            .catch(error => {
              if (error.response && error.response.status === 400) {
                this.errors = parseApiError(error, this.$i18n)
                window.scrollTo(0, 0)
              }
            })
        }
      } else {
        message.warning(this.$i18n.t('tender.please-choose-at-least-one-line'))
      }
    },
    save_tender () {
      if (this.tender.id) {
        this.errors = {}
        let hasError = false
        let chosenQty = 0

        for (let i = 0; i < this.chosen.length; i++) {
          api.patch(`/details/${this.chosen[i].id}/`, {
            ...this.chosen[i]
          })
          chosenQty += parseInt(this.chosen[i].qty)
        }

        if (this.chosen.length === 0 || chosenQty === 0) {
          hasError = true
          message.error(this.$i18n.t('tender.positive-qty'))
        }

        if (this.tender.start) {
          let current = new Date()
          let check = new Date(this.tender.start)

          if (check < current) {
            hasError = true
            this.errors['start'] = [this.$i18n.t('tender.start-time-should-be-at-future')]
          }
        }

        if (hasError === false) {
          api.patch(`/tenders/${this.tender.id}/`, {
            start: this.tender.start,
            finish: this.tender.finish,
            warehouse: this.tender.warehouse ? this.tender.warehouse.id : null,
            group: this.tender.group ? this.tender.group.id : null,
            status: 'WAITING',
            additional_info: this.tender.additional_info
          })
            .then(() => {
              setTimeout(() => {
                message.success(this.$i18n.t('tender.draft-added'))
                this.$router.push({name: 'tenders'})
              }, 1000)
            })
            .catch(error => {
              if (error.response && error.response.status === 400) {
                this.errors = parseApiError(error, this.$i18n)
                window.scrollTo(0, 0)
              }
            })
        }
      } else {
        message.warning(this.$i18n.t('tender.please-choose-at-least-one-line'))
      }
    }
  },
  beforeRouteEnter (to, from, next) {
    getData(to.params.id, to, data => {
      next(vm => {
        Object.assign(vm.$data, data)
      })
    })
  },
  beforeRouteUpdate (to, from, next) {
    getData(to.params.id, to, data => {
      Object.assign(this.$data, data)
      next()
    })
  }
}
</script>



// WEBPACK FOOTER //
// src/views/Tender.vue